import React from 'react';
import { useQueries } from '@tanstack/react-query';
import api from '../../../lib/api';
import { DataSource } from './Widget';
import { useFilters } from '../Filter';
import { applyFiltersToDataSource } from './utils/filterUtils';
import {
  transformCodeSecurityData,
  needsTransformation,
  isCodeSecurityData,
} from './utils/dataTransformers';

export interface DataLoaderResult {
  dataResults: Record<string, unknown>;
  isLoading: boolean;
  error: string | null;
}

// Hook to load data sources using the tools API with useQueries
export const useWidgetDataLoader = (
  dataSources?: DataSource[],
): DataLoaderResult => {
  const { filterState } = useFilters();

  // Apply filters to data sources
  const filteredDataSources = React.useMemo(() => {
    if (!dataSources) {
      return [];
    }
    return dataSources.map((dataSource) =>
      applyFiltersToDataSource(dataSource, filterState),
    );
  }, [dataSources, filterState]);

  // Create queries for all filtered data sources using useQueries
  const queries = useQueries({
    queries: (filteredDataSources || []).map((dataSource, index) => ({
      queryKey: [
        'widget-data',
        index,
        dataSource.toolName,
        JSON.stringify({ ...dataSource.parameters, intention: undefined }),
        JSON.stringify(filterState), // Include filter state in query key for cache invalidation
      ],
      queryFn: async () => {
        const { data } = await api.post('/api/core/pleri/tools/execute', {
          toolName: dataSource.toolName,
          parameters: dataSource.parameters,
        });
        return data;
      },
      enabled: !!filteredDataSources && filteredDataSources.length > 0,
      retry: false,
    })),
  });

  // Aggregate results
  const dataResults: Record<string, unknown> = {};
  let isLoading = false;
  let error: string | null = null;

  queries.forEach((query, index) => {
    if (query.isLoading) {
      isLoading = true;
    }

    if (query.error) {
      const errorMessage =
        query.error instanceof Error ? query.error.message : 'Unknown error';
      error = `Failed to load data source ${index}: ${errorMessage}`;
    }

    if (query.data) {
      // Apply data transformation if needed
      let transformedData = query.data;

      // Check if this is CodeSecurityData that needs transformation
      if (isCodeSecurityData(query.data) && needsTransformation(query.data)) {
        try {
          transformedData = transformCodeSecurityData(query.data);
        } catch (error) {
          console.warn('Failed to transform data:', error);
          // Use original data if transformation fails
        }
      }

      dataResults[`source_${index}`] = {
        data: {
          ...query.data.data,
          ...transformedData.data,
        },
      };
    }
  });

  return {
    dataResults,
    isLoading: queries.some((query) => query.isLoading),
    error,
  };
};
